// Screen-space layout only. Source latitude/longitude are never changed.
export const GAS_MARKER_WIDTH = 104 * 0.8;
export const GAS_MARKER_HEIGHT = 36 * 0.8;
const GAP = 7;
const STEP_X = GAS_MARKER_WIDTH + GAP;
const STEP_Y = GAS_MARKER_HEIGHT + GAP;
const MARGIN = 12;
export const DENSE_MARKER_COUNT = 7;
export interface ScreenPoint { x: number; y: number }
export interface GasMarkerPoint extends ScreenPoint { key: string }
export interface MarkerPlacement {
  members: GasMarkerPoint[];
  origin: ScreenPoint;
  point: ScreenPoint;
  cluster: boolean;
}
export interface ExpandedMarkers { key: string; page: number; keys?: string[] }

export function markersOverlap(a: ScreenPoint, b: ScreenPoint) {
  return Math.abs(a.x - b.x) < STEP_X - 0.01 && Math.abs(a.y - b.y) < STEP_Y - 0.01;
}

export function groupGasMarkers(points: GasMarkerPoint[]) {
  const remaining = new Set([...points].sort((a, b) => a.key.localeCompare(b.key)));
  const groups: GasMarkerPoint[][] = [];
  while (remaining.size) {
    const first = remaining.values().next().value!;
    const group = [first];
    remaining.delete(first);
    for (let i = 0; i < group.length; i++) {
      for (const candidate of remaining) {
        if (markersOverlap(group[i], candidate)) {
          remaining.delete(candidate);
          group.push(candidate);
        }
      }
    }
    groups.push(group.sort((a, b) => a.y - b.y || a.x - b.x || a.key.localeCompare(b.key)));
  }
  return groups;
}

const center = (members: GasMarkerPoint[]): ScreenPoint => ({
  x: members.reduce((sum, p) => sum + p.x, 0) / members.length,
  y: members.reduce((sum, p) => sum + p.y, 0) / members.length
});

export function layoutGasMarkers(points: GasMarkerPoint[], size: ScreenPoint, expanded: ExpandedMarkers | null = null) {
  // Centers leave space for full rectangles, zoom controls and attribution.
  const minX = MARGIN + GAS_MARKER_WIDTH / 2;
  const maxX = Math.max(minX, size.x - minX);
  const minY = 80;
  const maxY = Math.max(minY, size.y - MARGIN - GAS_MARKER_HEIGHT / 2 - 16);
  const clamp = (p: ScreenPoint) => ({ x: Math.max(minX, Math.min(maxX, p.x)), y: Math.max(minY, Math.min(maxY, p.y)) });
  const slots: ScreenPoint[] = [];
  for (let y = minY; y <= maxY; y += STEP_Y) {
    for (let x = minX; x <= maxX; x += STEP_X) slots.push({ x, y });
  }
  const pageSize = Math.max(1, Math.min(12, Math.floor((slots.length - 1) / 2)));
  const groups = groupGasMarkers(points);
  const active = expanded?.keys
    ? points.filter(p => expanded.keys!.includes(p.key)).sort((a, b) => a.y - b.y || a.x - b.x || a.key.localeCompare(b.key))
    : expanded ? groups.find(g => g.some(p => p.key === expanded.key)) : undefined;
  const pageCount = active ? Math.ceil(active.length / pageSize) : 0;
  const page = active?.length && expanded ? Math.min(pageCount - 1, Math.max(0, expanded.page < 0
    ? Math.floor(active.findIndex(p => p.key === expanded.key) / pageSize) : expanded.page)) : 0;
  const placements: MarkerPlacement[] = [];
  const overflow: GasMarkerPoint[] = [];
  // Reserve an overflow control so every record remains reachable at extreme density.
  const overflowSlot = slots[slots.length - 1];
  const occupied: ScreenPoint[] = overflowSlot ? [overflowSlot] : [];
  const place = (origin: ScreenPoint, limit = Infinity): ScreenPoint | undefined => {
    const home = clamp(origin);
    const candidates = [home];
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -3; dx <= 3; dx++) candidates.push(clamp({ x: home.x + dx * STEP_X, y: home.y + dy * STEP_Y }));
    }
    candidates.push(...slots);
    candidates.sort((a, b) => Math.hypot(a.x - origin.x, a.y - origin.y) - Math.hypot(b.x - origin.x, b.y - origin.y));
    const target = candidates.find(p => Math.hypot(p.x - origin.x, p.y - origin.y) <= limit && !occupied.some(q => markersOverlap(p, q)));
    if (target) occupied.push(target);
    return target;
  };
  const addCluster = (members: GasMarkerPoint[]) => {
    const origin = center(members);
    const point = place(origin);
    if (point) placements.push({ members, origin, point, cluster: true });
    else overflow.push(...members);
  };
  // Expanded labels get priority; remaining members are reachable through paging.
  if (active) {
    for (const member of active.slice(page * pageSize, (page + 1) * pageSize)) {
      const point = place(member);
      if (point) placements.push({ members: [member], origin: member, point, cluster: false });
      else overflow.push(member);
    }
  }
  const activeKeys = new Set(active?.map(p => p.key));
  for (const group of groupGasMarkers(points.filter(p => !activeKeys.has(p.key)))) {
    if (group.length >= DENSE_MARKER_COUNT) { addCluster(group); continue; }
    const occupiedStart = occupied.length;
    const placed = group.map(member => ({ member, point: place(member, 140) }));
    if (placed.some(p => !p.point)) {
      occupied.length = occupiedStart;
      addCluster(group);
    } else {
      placements.push(...placed.map(({ member, point }) => ({ members: [member], origin: member, point: point!, cluster: false })));
    }
  }
  if (overflow.length && overflowSlot) placements.push({ members: overflow, origin: center(overflow), point: overflowSlot, cluster: true });
  return { placements, page, pageCount, expandedCount: active?.length ?? 0 };
}
